import { Badge, Button, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { useCheckLogin, useSignOut } from '_api';
import { Admin, Therapist } from '_api/types';
import {
  ACCOUNT_TYPE,
  AdminRouteMap,
  InitialRouteMap,
  PatientRouteMap,
} from '_constants';
import { HeaderMenu } from 'components/molecules';
import { ContactSupportModal } from 'components/organisms/modals';
import React, { useMemo, useState } from 'react';
import { FaCircleQuestion } from 'react-icons/fa6';
import { GoTriangleDown, GoTriangleUp } from 'react-icons/go';
import { MdAccountCircle, MdEmail } from 'react-icons/md';
import { useNavigateTo } from 'utils';
import { useMessage } from 'utils/hooks';

import { HeaderButtons, HeaderToolbar, StyledAppBar } from './styles';
import { HeaderMenuItem } from './types';

const ACCOUNT = 'account';

const getPatientAndLibraryMenuItems = (
  account: Admin | Therapist | undefined,
  navigate: (route: string, props?: string | undefined) => void,
  handleClose: () => void,
) => {
  return [
    {
      title: 'Patients',
      options: [
        {
          label:
            account && account.accountType === ACCOUNT_TYPE.THERAPIST
              ? 'My Patients'
              : 'Active',
          onClick: () => {
            navigate(AdminRouteMap.PATIENTS);
            handleClose();
          },
        },
        {
          label: 'Find Patients',
          hide: account && account.accountType !== ACCOUNT_TYPE.THERAPIST,
          onClick: () => {
            navigate(PatientRouteMap.SEARCH);
            handleClose();
          },
        },
        {
          label: 'Archived',
          hide: account && account.accountType === ACCOUNT_TYPE.THERAPIST,
          onClick: () => {
            navigate(PatientRouteMap.ARCHIVED);
            handleClose();
          },
        },
      ].filter((option) => !option.hide),
    },
    {
      title: 'Library',
      options: [
        {
          label: 'My library',
          onClick: () => {
            navigate(AdminRouteMap.LIBRARY);
            handleClose();
          },
        },
        {
          label: 'Exercises',
          onClick: () => {
            navigate(AdminRouteMap.EXERCISES);
            handleClose();
          },
        },
        {
          label: 'Functional Challenges',
          onClick: () => {
            navigate(AdminRouteMap.CHALLENGES);
            handleClose();
          },
        },
        {
          label: 'Resources',
          onClick: () => {
            navigate(AdminRouteMap.RESOURCES);
            handleClose();
          },
        },
      ],
    },
  ];
};

const getMenuOption = (
  label: string,
  onClick: (route: string) => void,
  route: string,
) => ({
  label: label,
  onClick: () => {
    onClick(route);
  },
});

export const Header = () => {
  const navigate = useNavigateTo();
  const theme = useTheme();
  const { data: account } = useCheckLogin();
  const { mutateAsync: signOut } = useSignOut();
  const { unreadMessageIdList } = useMessage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openContactSupport, setOpenContactSupport] = useState(false);

  const handleClose = () => {
    setAnchorEl(null);
    setOpenMenu(null);
  };

  const navigateAndClose = (route: string) => {
    navigate(route);
    handleClose();
  };

  const patientAndLibraryMenuItems = useMemo(
    () => getPatientAndLibraryMenuItems(account, navigate, handleClose),
    [account],
  );
  const userOption = useMemo(
    () => getMenuOption('Users', navigateAndClose, AdminRouteMap.USERS),
    [account],
  );
  const organizationsOption = useMemo(
    () => getMenuOption('Organizations', navigateAndClose, AdminRouteMap.ORGANIZATIONS),
    [account],
  );
  const archiveOrganizationsOption = useMemo(
    () =>
      getMenuOption(
        'Archived Organizations',
        navigateAndClose,
        AdminRouteMap.ARCHIVE_ORGANIZATIONS,
      ),
    [account],
  );
  const hospitalOption = useMemo(
    () =>
      getMenuOption('Hospitals and Clinics', navigateAndClose, AdminRouteMap.HOSPITALS),
    [account],
  );
  const archiveHospitalOption = useMemo(
    () =>
      getMenuOption(
        'Archived Hospitals and Clinics',
        navigateAndClose,
        AdminRouteMap.ARCHIVE_HOSPITALS,
      ),
    [account],
  );

  const handleMenu = (title: string) => (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(openMenu === title ? null : title);
  };

  const handleMessage = () => {
    navigate(AdminRouteMap.MESSAGE);
  };

  const logOut = async () => {
    try {
      await signOut({});
    } finally {
      navigate(InitialRouteMap.LOGIN);
    }
  };

  const handleProfile = () => {
    navigate(AdminRouteMap.PROFILE, account?.id.toString());
  };

  const adminMenu: HeaderMenuItem = {
    title: 'Admin',
    options:
      account && account.accountType === ACCOUNT_TYPE.ADMIN
        ? [
            userOption,
            organizationsOption,
            hospitalOption,
            archiveHospitalOption,
            archiveOrganizationsOption,
          ]
        : account && account.accountType === ACCOUNT_TYPE.ORG_ADMIN
          ? [userOption, hospitalOption]
          : [userOption],
  };

  const menuItems = useMemo(
    () =>
      account && account.accountType === ACCOUNT_TYPE.THERAPIST
        ? patientAndLibraryMenuItems
        : [...patientAndLibraryMenuItems, adminMenu],
    [account],
  );

  return (
    <StyledAppBar position="static">
      <HeaderToolbar
        sx={{
          paddingLeft: '64px !important',
          paddingRight: '4rem !important',
        }}
      >
        <Button onClick={() => navigate(AdminRouteMap.USERS)} sx={{ padding: '0px' }}>
          <img src="/img/logo-navigation.png" alt={''} style={{ maxWidth: '50%' }} />
        </Button>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          YourSteps Health
        </Typography>
        <div
          style={{ flexGrow: 1, display: 'flex', gap: '16px', justifyContent: 'center' }}
        >
          {menuItems.map((item) => (
            <React.Fragment key={item.title}>
              <Button
                onClick={handleMenu(item.title)}
                endIcon={openMenu === item.title ? <GoTriangleUp /> : <GoTriangleDown />}
                sx={{
                  color:
                    openMenu === item.title
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                }}
              >
                {item.title}
              </Button>
              <HeaderMenu
                anchorEl={openMenu === item.title ? anchorEl : null} // Pass the anchorEl only if this menu should be open
                handleClose={handleClose}
                options={item.options}
                title={item.title}
              />
            </React.Fragment>
          ))}
        </div>
        <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'end' }}>
          {account?.accountType === ACCOUNT_TYPE.THERAPIST ? (
            <HeaderButtons onClick={handleMessage}>
              <Badge badgeContent={unreadMessageIdList.length} color="error">
                <MdEmail />
              </Badge>
            </HeaderButtons>
          ) : null}
          <HeaderButtons onClick={() => setOpenContactSupport(true)}>
            <FaCircleQuestion />
          </HeaderButtons>
          <HeaderButtons onClick={handleMenu(ACCOUNT)}>
            <MdAccountCircle />
          </HeaderButtons>
          <Menu
            id={ACCOUNT}
            anchorEl={openMenu === ACCOUNT ? anchorEl : null}
            open={openMenu === ACCOUNT}
            onClose={handleClose}
          >
            <MenuItem onClick={handleProfile}>Profile</MenuItem>
            <MenuItem onClick={logOut}>Log Out</MenuItem>
          </Menu>
        </div>
      </HeaderToolbar>
      <ContactSupportModal
        open={openContactSupport}
        onClose={() => setOpenContactSupport(false)}
      />
    </StyledAppBar>
  );
};
